import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Button,
  Modal,
  Chip,
  Avatar,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import {
  getSystemExplanations,
  deleteSystemExplanation,
} from "../../../../Services/SystemExplanationsApi";
import { API_BASE_URL_images } from "../../../../Services/apiConfig";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddSystemExplanations from "./AddSystemExplanations";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import EditSystemExplanation from "./EditSystemExplanation";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddExplanationImages from "./AddExplanationImages";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AddExplanationVideos from "./AddExplanationVideos";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ImageIcon from "@mui/icons-material/Image";
import CategoryIcon from "@mui/icons-material/Category";
import InfoIcon from "@mui/icons-material/Info";

const SystemExplanationsTap = () => {
  const [explanations, setExplanations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null); // Store the data of the explanation to be edited
  const [addImageModalOpen, setAddImageModalOpen] = useState(false);
  const [addVideoModalOpen, setAddVideoModalOpen] = useState(false);
  const [selectedExplanationId, setSelectedExplanationId] = useState(null);
  const [selectedExplanationForVideo, setSelectedExplanationForVideo] =
    useState(null);

  const fetchExplanations = async () => {
    try {
      const response = await getSystemExplanations();

      const data = response?.data?.data; // ✅ التعديل هنا

      if (Array.isArray(data)) {
        setExplanations(data);
      } else {
        console.warn("Invalid data format:", response);
        setExplanations([]);
      }
    } catch (error) {
      console.error("Error fetching system explanations:", error);
      setError("فشل في جلب بيانات الشروحات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExplanations();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا الشرح؟")) {
      try {
        await deleteSystemExplanation(id); // Call the delete function
        alert("✅ تم حذف الشرح بنجاح!");
        fetchExplanations(); // Refresh the data after deletion
      } catch (error) {
        console.error("❌ Error deleting explanation:", error);
        alert("حدث خطأ أثناء حذف الشرح.");
      }
    }
  };

  const handleEdit = (id) => {
    const explanationToEdit = explanations.find(
      (explanation) => explanation.id === id
    );
    setEditId(id); // Set the ID to be passed to the edit component
    setEditData(explanationToEdit); // Set the data to be passed to the edit component
    setEditModalOpen(true); // Open the edit modal
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleSuccess = () => {
    setOpenModal(false); // Close the dialog
    fetchExplanations(); // Refresh the data
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditId(null); // Clear the ID when closing the modal
    setEditData(null); // Clear the data when closing the modal
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false); // Close the edit modal
    fetchExplanations(); // Refresh the data
  };

  const handleAddImage = (id) => {
    setSelectedExplanationId(id); // Set the ID of the explanation
    setAddImageModalOpen(true); // Open the add image modal
  };

  const handleAddImageModalClose = () => {
    setAddImageModalOpen(false);
    setSelectedExplanationId(null); // Clear the selected explanation ID
  };

  const handleAddImageSuccess = () => {
    setAddImageModalOpen(false); // Close the add image modal
    fetchExplanations(); // Refresh the data
  };

  const handleAddVideo = (id) => {
    setSelectedExplanationForVideo(id); // Set the ID of the explanation
    setAddVideoModalOpen(true); // Open the add video modal
  };

  const handleAddVideoModalClose = () => {
    setAddVideoModalOpen(false);
    setSelectedExplanationForVideo(null); // Clear the selected explanation ID
  };

  const handleAddVideoSuccess = () => {
    setAddVideoModalOpen(false); // Close the add video modal
    fetchExplanations(); // Refresh the data
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 32, color: "#1976d2" }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", color: "#333" }}
          >
            شروحات النظام
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {!loading && !error && (
            <Chip
              label={`${explanations.length} شرح`}
              color="primary"
              sx={{ fontWeight: "bold", fontSize: "1rem" }}
            />
          )}
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: "bold",
              background:
                "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              "&:hover": {
                boxShadow: 6,
                transform: "translateY(-2px)",
                transition: "all 0.3s",
              },
            }}
          >
            إضافة شرح جديد
          </Button>
        </Box>
      </Box>

      {/* Modal إضافة شرح */}
      <Modal
        open={openModal}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleCloseModal();
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "90%",
            maxWidth: 800,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddCircleIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                إضافة شرح جديد
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <AddSystemExplanations onSuccess={handleSuccess} />
          </CardContent>
        </Card>
      </Modal>

      {/* Modal عرض الصورة */}
      <Modal open={imageModalOpen} onClose={handleImageModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 2,
            borderRadius: 2,
            boxShadow: 24,
            maxWidth: "90%",
            maxHeight: "90%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                borderRadius: 8,
              }}
            />
          )}
        </Box>
      </Modal>

      {/* Modal تعديل شرح */}
      <Modal
        open={editModalOpen}
        onClose={handleEditModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "90%",
            maxWidth: 800,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#9c27b0",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                تعديل الشرح
              </Typography>
            </Box>
            <IconButton
              onClick={handleEditModalClose}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {editId && editData && (
              <EditSystemExplanation
                explanationId={editId}
                existingData={editData}
                onSuccess={handleEditSuccess}
              />
            )}
          </CardContent>
        </Card>
      </Modal>

      {/* Modal إضافة صورة */}
      <Modal
        open={addImageModalOpen}
        onClose={handleAddImageModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "90%",
            maxWidth: 800,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#4caf50",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddPhotoAlternateIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                إضافة صور للشرح
              </Typography>
            </Box>
            <IconButton
              onClick={handleAddImageModalClose}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {selectedExplanationId && (
              <AddExplanationImages
                explanationId={selectedExplanationId}
                onSuccess={handleAddImageSuccess}
              />
            )}
          </CardContent>
        </Card>
      </Modal>

      {/* Modal إضافة فيديو */}
      <Modal
        open={addVideoModalOpen}
        onClose={handleAddVideoModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "90%",
            maxWidth: 800,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#ff9800",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VideoLibraryIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                إضافة فيديو للشرح
              </Typography>
            </Box>
            <IconButton
              onClick={handleAddVideoModalClose}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {selectedExplanationForVideo && (
              <AddExplanationVideos
                explanationId={selectedExplanationForVideo}
                onSuccess={handleAddVideoSuccess}
              />
            )}
          </CardContent>
        </Card>
      </Modal>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            جاري تحميل الشروحات...
          </Typography>
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{ fontSize: "1.1rem", py: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      ) : explanations.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  العنوان
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  الوصف
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  النوع
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  الإصدار
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  الحالة
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  الصورة
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", fontSize: "1rem" }}
                >
                  الإجراءات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {explanations.map((explanation) => (
                <TableRow
                  key={explanation.id}
                  sx={{
                    "&:hover": {
                      bgcolor: "#f9f9f9",
                      transition: "all 0.2s",
                    },
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#1976d2",
                          width: 35,
                          height: 35,
                        }}
                      >
                        <DescriptionIcon fontSize="small" />
                      </Avatar>
                      <Typography sx={{ fontWeight: "bold" }}>
                        {explanation.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      {explanation.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<CategoryIcon />}
                      label={explanation.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<InfoIcon />}
                      label={explanation.version}
                      size="small"
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        explanation.is_active === 1 ? (
                          <CheckCircleIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                      label={
                        explanation.is_active === 1
                          ? "نشط"
                          : "غير نشط"
                      }
                      color={
                        explanation.is_active === 1
                          ? "success"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {explanation.image ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Avatar
                          src={`${API_BASE_URL_images}/${explanation.image}`}
                          alt={explanation.title}
                          variant="rounded"
                          sx={{
                            width: 50,
                            height: 50,
                            cursor: "pointer",
                            boxShadow: 2,
                          }}
                          onClick={() =>
                            handleImageClick(
                              `${API_BASE_URL_images}/${explanation.image}`
                            )
                          }
                        />
                        <Tooltip title="تكبير الصورة">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() =>
                              handleImageClick(
                                `${API_BASE_URL_images}/${explanation.image}`
                              )
                            }
                            sx={{
                              bgcolor: "#e3f2fd",
                              "&:hover": { bgcolor: "#bbdefb" },
                            }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: "#e0e0e0",
                        }}
                      >
                        <ImageIcon />
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Tooltip title="تعديل الشرح">
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() => handleEdit(explanation.id)}
                          sx={{
                            bgcolor: "#f3e5f5",
                            "&:hover": { bgcolor: "#e1bee7" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف الشرح">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(explanation.id)}
                          sx={{
                            bgcolor: "#ffebee",
                            "&:hover": { bgcolor: "#ffcdd2" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="إضافة صور">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleAddImage(explanation.id)}
                          sx={{
                            bgcolor: "#e8f5e9",
                            "&:hover": { bgcolor: "#c8e6c9" },
                          }}
                        >
                          <AddPhotoAlternateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="إضافة فيديو">
                        <IconButton
                          color="warning"
                          size="small"
                          onClick={() => handleAddVideo(explanation.id)}
                          sx={{
                            bgcolor: "#fff3e0",
                            "&:hover": { bgcolor: "#ffe0b2" },
                          }}
                        >
                          <VideoLibraryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <DescriptionIcon
            sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            لا توجد شروحات متوفرة
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SystemExplanationsTap;
